import { IdeonApiSdkInternal } from '@ideon-api/sdk-internal'
import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useRef } from 'react'

export interface SdkThrownError {
  metadata?: { status?: number }
  error?: string
  raw?: ArrayBuffer
}

// The generated SDK dispatches to a typed per-status error class only when
// its content-type sniffing recognizes the response as JSON -- and that
// sniffing does an exact `=== 'application/json'` match, which fails against
// Rails' default `application/json; charset=utf-8`. Every error response
// from this API carries that charset, so the SDK falls through to a generic
// `HttpError` instead: `.error` on that fallback is just the HTTP status
// text ("Conflict", "Not Found"), never the JSON body's own `error`/`message`
// fields, and `.message` is a placeholder ("Unexpected response body for
// error status..."). The fallback is the only place a raw `.raw` ArrayBuffer
// is ever set (typed error classes carry `.response`, not `.raw`), so use it
// to detect the fallback and reparse the real body ourselves.
//
// The reparsed body is always the server's verbatim JSON -- snake_case
// (`survivor_slug`, `loser_slug`, `owner_slug`), never the SDK's camelCase --
// since the typed classes that would camelCase it never run. A caller must
// read `body.owner_slug`, not `body.ownerSlug`.
//
// `rawUnparseable` is set when `.raw` is present but isn't JSON (a 502/504
// HTML page from the ingress, a Rails HTML 500 -- neither carries a `.raw`
// that decodes). It's the caller's signal that `error.message` is the
// generic-fallback placeholder, not real server text, so `sdkErrorMessage`
// (below) skips that branch and returns its own fallback instead.
export function parsedSdkErrorBody(
  error: unknown
): { status?: number; body: Record<string, unknown>; rawUnparseable?: boolean } {
  if (!error || typeof error !== 'object') return { body: {} }
  const thrown = error as SdkThrownError
  const status = thrown.metadata?.status
  // Duck-typed rather than `instanceof ArrayBuffer` -- an ArrayBuffer built
  // in a different realm (a jsdom test environment vs. this module's own
  // globals, for instance) fails `instanceof` even though it's a perfectly
  // usable ArrayBuffer.
  const raw = thrown.raw
  if (raw && typeof raw === 'object' && typeof (raw as ArrayBuffer).byteLength === 'number') {
    try {
      const parsed: unknown = JSON.parse(new TextDecoder().decode(raw))
      return { status, body: parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {} }
    } catch {
      return { status, body: {}, rawUnparseable: true }
    }
  }
  return { status, body: thrown as unknown as Record<string, unknown> }
}

// Some SDK error bodies (e.g. a 404 `{error: 'account_not_found'}`) carry no
// `message` key, so the SDK's thrown `.message` is `''` -- falsy, so
// `{error && <Alert>}` silently renders nothing. Fall back to the body's own
// `message`/`error` field (reparsed via `parsedSdkErrorBody` -- see its
// comment for why the SDK's own `.message`/`.error` can't be trusted), then
// to a caller-supplied default, so every catch site renders *something*.
export function sdkErrorMessage(error: unknown, fallback: string): string {
  const { body, rawUnparseable } = parsedSdkErrorBody(error)
  if (typeof body.message === 'string' && body.message) return body.message
  if (typeof body.error === 'string' && body.error) return body.error
  // A present-but-unparseable `.raw` already proves this is the generic
  // fallback shape, so `error.message` here is known to be the
  // "Unexpected response body..." placeholder, not real server text --
  // the caller's own fallback is the better answer.
  if (!rawUnparseable && error instanceof Error && error.message) return error.message
  return fallback
}

// A 404 on an account lookup can carry `merged_into` (the successor slug,
// PD-30247) when the account was archived by a merge -- the requested slug
// is real, it just isn't the account's slug anymore. Distinct from a plain
// "no such account" 404, which has no `merged_into` key at all.
export function mergedIntoFromError(error: unknown): string | undefined {
  const { status, body } = parsedSdkErrorBody(error)
  if (status !== 404) return undefined
  return typeof body.merged_into === 'string' ? body.merged_into : undefined
}

// Auth0 hands out a short-lived access token plus a long-lived refresh token,
// and a dead refresh token is invisible until a call needs a new access token.
// `Auth0Provider` boots by calling `checkSession()`, which swallows every
// error (`catch (_) {}` in auth0-spa-js), then `getUser()` -- which decodes
// the *cached* id_token out of localStorage and so still returns a user. The
// provider therefore reports `isAuthenticated: true` with no `error`, AuthGate
// renders the app, and every request instead fails inside
// `getAccessTokenSilently` with "Unknown or invalid refresh token." A browser
// reload repeats that exactly, because the dead refresh token is still in
// localStorage -- there is no path back to a working session except sending
// the user to Auth0. Redirect on the error codes that mean this session can't
// be renewed without them (PD-30530).
const REAUTH_ERROR_CODES = [
  'login_required',
  'invalid_grant',
  'missing_refresh_token',
  'consent_required',
  // A refresh grant can return this when an MFA policy changes under a live
  // session; the user can only satisfy it interactively.
  'mfa_required',
]

// auth0-react's `tokenError` returns an `Error` instance untouched, and
// auth0-spa-js' `GenericError extends Error` carries the OAuth2 `error` code
// alongside `message`, so the code survives to here. Anything else (a network
// blip, a 5xx from the token endpoint) is a plain Error with no `.error` and
// must NOT bounce the user out of the app.
function requiresReauth(error: unknown): boolean {
  const code = (error as { error?: unknown } | null)?.error
  return typeof code === 'string' && REAUTH_ERROR_CODES.includes(code)
}

// Module-level, not a ref: a page typically has several hooks in flight, and
// they all fail on the same dead token within the same tick. A second
// `loginWithRedirect` would overwrite the first one's pending transaction
// state and break the callback. Cleared by the page load that the redirect
// itself causes; `_resetRedirectGuard` exists only so tests start clean.
let redirecting = false

export function _resetRedirectGuard(): void {
  redirecting = false
}

export function useAccessToken() {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0()

  return useCallback(async () => {
    try {
      return await getAccessTokenSilently()
    } catch (error) {
      if (requiresReauth(error) && !redirecting) {
        redirecting = true
        // Not awaited: `loginWithRedirect` resolves only when the browser
        // navigates away, so awaiting it would hold the caller open until
        // the page is gone anyway. Rethrow so the caller's own error path
        // still settles instead of hanging.
        // The navigation itself is what clears `redirecting`, by unloading
        // the page. If loginWithRedirect rejects instead (sessionStorage
        // blocked or over quota, so its pending transaction can't be
        // written), nothing navigates -- release the flag rather than leave
        // the tab unable to ever re-auth again.
        void loginWithRedirect({
          appState: { returnTo: window.location.pathname + window.location.search + window.location.hash },
        }).catch(() => {
          redirecting = false
        })
      }
      throw error
    }
  }, [getAccessTokenSilently, loginWithRedirect])
}

// Same-origin SPA (served by the auth service itself), so baseUrl is just
// the current origin. The user's own Auth0 access token is used directly --
// no server-side token exchange needed (unlike ideoninsights' M2M client,
// which fetches a service token from a proxy endpoint).
export function useAuthServiceClient() {
  const getToken = useAccessToken()
  const clientRef = useRef<IdeonApiSdkInternal | null>(null)

  return useCallback(async () => {
    const token = await getToken()
    if (!clientRef.current) {
      clientRef.current = new IdeonApiSdkInternal({
        baseUrl: window.location.origin,
        token,
        // `useApiFetch` (the old raw-fetch client this replaces) applied a
        // 15s AbortController timeout to every call -- the SDK only attaches
        // one when `timeoutMs` is set, so this must be explicit or no call in
        // this app can ever time out.
        timeoutMs: 15000,
        // The SDK's default retries POST on 5xx/408/429, which raw `fetch`
        // never did. Every real caller here mutates (mergeAccounts,
        // dismissDuplicateCandidate, bindAccountIdentifier) and none should be
        // blindly retried, so restrict retries to GET.
        retry: { attempts: 3, httpMethodsToRetry: ['GET'] },
      })
    } else {
      clientRef.current.token = token
    }
    return clientRef.current
  }, [getToken])
}
